import { useCallback, useRef, useState } from 'react'
import IssuesService from '../../../services/api/IssuesService'
import { showToast } from '../../shared/Toast'

const SUCCESS_MSG = 'La modification de l’alerte a bien été effectuée.'
const ERROR_MSG = 'Une erreur est survenue lors de la modification de l’alerte.'
const USER_IDS_KEY = 'user_ids'
const ISSUE_STATUS_ID_KEY = 'issue_status_id'

const noop = () => {
  // Intentionally empty default callback
}

const formatErrors = error => {
  if (!error)
    return ERROR_MSG
  if (typeof error.message === 'string' && error.message.length > 0)
    return error.message
  return ERROR_MSG
}

/**
 * Manages optimistic PATCHes on issues (status + attribution).
 *
 * Flow per mutation:
 *  1. caller calls `updateStatus(id, statusId, issueStatus)` / `updateAttribution(id, userIds)`
 *  2. hook invokes `onSuccess({ issueId, issueStatus?, userIds?, optimistic: true })`
 *     BEFORE the network call so the table updates immediately
 *  3. if PATCH resolves 2xx -> success toast, `onSuccess({ ..., optimistic: false, issue })`
 *  4. if PATCH rejects (network or 422) -> error toast,
 *     `onError({ issueId, message })` so the caller rolls back local state
 */
const useIssueMutations = (collectId, callbacks = {}) => {
  const { onSuccess = noop, onError = noop, toastContainerId = null } = callbacks
  const [updatingIds, setUpdatingIds] = useState(() => new Set())
  // Keep latest callbacks in a ref so the returned functions stay stable.
  const callbacksRef = useRef({ onSuccess, onError, toastContainerId })
  callbacksRef.current = { onSuccess, onError, toastContainerId }

  const markUpdating = useCallback((issueId, isUpdating) => {
    setUpdatingIds(previous => {
      const next = new Set(previous)
      if (isUpdating)
        next.add(issueId)
      else
        next.delete(issueId)
      return next
    })
  }, [])

  const runMutation = useCallback(async(issueId, payload, optimisticPatch) => {
    const refs = callbacksRef.current
    refs.onSuccess({ issueId, ...optimisticPatch, optimistic: true })
    markUpdating(issueId, true)
    try {
      const response = await IssuesService.updateIssue(collectId, issueId, payload)
      const issue = response?.issue || null
      refs.onSuccess({ issueId, ...optimisticPatch, optimistic: false, issue })
      showToast(SUCCESS_MSG, 'success', refs.toastContainerId)
      return { ok: true, issue }
    } catch (error) {
      const message = formatErrors(error)
      refs.onError({ issueId, message })
      showToast(ERROR_MSG, 'error', refs.toastContainerId)
      return { ok: false, error }
    } finally {
      markUpdating(issueId, false)
    }
  }, [collectId, markUpdating])

  const updateStatus = useCallback((issueId, issueStatusId, issueStatus) => {
    runMutation(issueId, { [ISSUE_STATUS_ID_KEY]: issueStatusId }, { issueStatus })
  }, [runMutation])

  const updateAttribution = useCallback((issueId, userIds) => {
    const payload = { [USER_IDS_KEY]: userIds }
    runMutation(issueId, payload, { userIds })
  }, [runMutation])

  const isUpdating = useCallback(issueId => updatingIds.has(issueId), [updatingIds])

  return { updateStatus, updateAttribution, isUpdating, updatingIds }
}

export default useIssueMutations
